export default defineStore('useLocaleStore', () => {
  const localePath = useLocalePath()
  const { $i18n } = useNuxtApp()
  const { locales, setLocale, strategy, getBrowserLocale, localeCodes, fallbackLocale } = $i18n
  let { defaultLocale } = $i18n

  const getRouteBaseName = useRouteBaseName()
  const route = useRoute()
  const activeLocaleTypes = ref(new Set<string>([]))
  const localesAreSync = computed(() => {
    return activeLocaleTypes.value.size === 1
  })

  const setDefaultLocale = () => {
    if (strategy !== 'no_prefix') {
      return defaultLocale
    }

    const browserLocale = getBrowserLocale()
    if (defaultLocale !== browserLocale) {
      defaultLocale = browserLocale as any
    }

    if (!localeCodes.value.includes(defaultLocale as any)) {
      defaultLocale = fallbackLocale.value as any
    }

    return defaultLocale
  }

  const uiLocale = useCookie('i18n_redirected', {
    default: () => setDefaultLocale(),
    watch: true,
  })

  const contentLocale = useCookie('content-locale', {
    default: () => setDefaultLocale(),
    watch: true,
  })

  const urlLocale = computed(() => {
    const path = route.path

    for (const locale of locales.value) {
      if (path.startsWith(`/${locale.code}/`) || path === `/${locale.code}`) {
        return locale.code
      }
    }
    return defaultLocale
  })

  const reloadBrowser = async (locale: string, force: boolean = false) => {
    if (!force && strategy !== 'no_prefix') {
      return
    }

    await navigateTo(
      localePath({ name: getRouteBaseName(route) }, locale as any),
      { external: true },
    )
  }

  // Note to self:
  //
  // If the locales are mystically and mysteriously sync without any reason,
  // it's probably because you have both a non sync app (admin)
  // and a sync app (www) loaded at the same time on localhost
  // They share the same cookies and when you change the admin locale, it also
  // change the locale of the www app... where ui and content are sync. Therefore,
  // it propagates to the non sync app and you feel like you are crazy.

  // Anyway, to fix this, just close the www app. Terminating the process is not enough,
  // it does its thing on the client side, so any tab should be closed too.

  // Or eventually just know that and don't care, because it only happens on localhost
  // in this very specific case.

  watch(contentLocale, async (newValue) => {
    if (!import.meta.client) {
      return
    }

    if (localesAreSync.value) {
      setLocale(newValue as any)
    }

    contentLocale.value = newValue
    await reloadBrowser(newValue, true)
  })

  watch(uiLocale, async (newValue) => {
    if (!import.meta.client) {
      return
    }

    if (localesAreSync.value) {
      contentLocale.value = newValue
    }

    setLocale(newValue as any)
    await reloadBrowser(newValue)
  })

  if (import.meta.server && strategy !== 'no_prefix') {
    contentLocale.value = urlLocale.value
    uiLocale.value = urlLocale.value
  }

  return { activeLocaleTypes, contentLocale, uiLocale }
})
